// Global
import React from 'react';

// Local
import { CardListing } from '@generated/CardListing/ThreePackCards.model';
import { ComponentParams, GetComponentServerProps, Item } from '@sitecore-content-sdk/nextjs';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import { ThreePackCards } from './ThreePackCards';
// import { CardFields, ContentGridStaticProps } from '../CardListingShared';
import { CardFields } from '../CardListingShared';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';

// Data fetching
import {
  ContentListWithOverrides,
  DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
  getContentListWithOverrides,
} from 'lib/utils/components/content-list-data';

import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { useDefaultCardDetailLevel } from 'lib/hooks/useSiteSettings';

type ThreePackCardsProps = Omit<CardListing.ThreePackCards.ThreePackCards_Component, 'params'> & {
  staticProps: ContentListWithOverrides | null;
  params?: ComponentParams & {
    cardDetails: {
      fieldsToShow: Item[];
    };
    gridAspectRatio: Item;
  };
};

const ThreePackCardsContent = (props: ThreePackCardsProps) => {
  // Get the default card detail level from site settings (handles both flat and nested shapes)
  const defaultCardDetailLevel = useDefaultCardDetailLevel();

  const validCardFields: CardFields[] = [
    'Image',
    'Eyebrow',
    'Title',
    'Rating',
    'KitchenTested',
    'PrepTime',
    'TotalTime',
    'Ingredients',
    'Servings',
    'Description',
    'Favorite',
    'Author',
    'Headline',
    'Labels',
    'PublishDate',
    'CreatedDate',
    'ImageLabel',
  ];

  const preambleFields = usePreambleFields({
    fields: props.fields,
    ctaComponentClassOverrides: {
      cta1: 'component-content-grid-color-button-color-1',
      cta2: 'component-content-grid-color-button-color-2',
    },
  });

  // Always filter and fallback deterministically
  // Priority: 1) cardDetails from component params, 2) defaultCardDetailLevel from site settings, 3) default fields
  const sitecoreFields = props.params?.cardDetails?.fieldsToShow?.length
    ? props.params.cardDetails.fieldsToShow.map((x: Item) => x.name as CardFields)
    : defaultCardDetailLevel?.length
      ? defaultCardDetailLevel.map((x) => x.name as CardFields)
      : [];
  const filteredFields = sitecoreFields.filter((field) => validCardFields.includes(field));
  const fieldsToShow =
    filteredFields.length > 0
      ? filteredFields
      : (['Image', 'Title', 'Description'] as CardFields[]);
  const gridAspectRatio = getEnumValue<ImageAspectRatio>(props.params?.gridAspectRatio);

  return (
    <ThreePackCards
      {...preambleFields}
      cards={props.staticProps?.result?.Results || []}
      contentOverrides={props.staticProps?.contentOverrides || {}}
      fallbackImageUrl={props.staticProps?.fallbackImageUrl}
      fieldsToShow={fieldsToShow}
      cardAlignment={props.params?.cardAlignment as ContentGridCardAlignment}
      gridAspectRatio={gridAspectRatio}
    />
  );
};

export const Default = (props: ThreePackCardsProps) => {
  return <ThreePackCardsContent {...props} />;
};

export const getComponentServerProps: GetComponentServerProps = async (rendering, layoutData) => {
  return {
    staticProps: await getContentListWithOverrides(rendering, layoutData, {
      maxCards: 3,
      gridAspectRatio:
        getEnumValue<ImageAspectRatio>(
          rendering.params?.gridAspectRatio as unknown as Parameters<typeof getEnumValue>[0]
        ) ?? DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
    }),
  };
};
